/**
 * Chatbot analytics — reporting-service (`/api/v1/reporting`).
 * Workspace + tenant isolation: Bearer and X-Workspace-Id via reportingApiFetch only.
 */

import { reportingApiFetch, reportingPath } from "@/lib/reportingApi";
import { ReportingAnalyticsDateRange } from "@/pages/analytics/lib/reportingAnalyticsDateRange";

export class BotReportingApiClient {
  /**
   * @param {{ startDate: string, endDate: string }} range
   * @returns {ReportingAnalyticsDateRange}
   */
  _window(range) {
    return new ReportingAnalyticsDateRange(range);
  }

  /**
   * @param {{ startDate: string, endDate: string }} range
   * @param {RequestInit} [init]
   */
  getDailyChats(range, init = {}) {
    return reportingApiFetch(`${reportingPath("/dailychats")}${this._window(range).toWindowQueryString()}`, init);
  }

  getUniqueConversations(range, init = {}) {
    return reportingApiFetch(
      `${reportingPath("/unique_conversations")}${this._window(range).toWindowQueryString()}`,
      init,
    );
  }

  getReturningUsers(range, init = {}) {
    return reportingApiFetch(
      `${reportingPath("/returning_users")}${this._window(range).toWindowQueryString()}`,
      init,
    );
  }

  getAvgChatTime(range, init = {}) {
    return reportingApiFetch(`${reportingPath("/avg_chat_time")}${this._window(range).toWindowQueryString()}`, init);
  }

  getCompletionRate(range, init = {}) {
    return reportingApiFetch(
      `${reportingPath("/completion_rate")}${this._window(range).toWindowQueryString()}`,
      init,
    );
  }

  getDropoffRate(range, init = {}) {
    return reportingApiFetch(`${reportingPath("/dropoff_rate")}${this._window(range).toWindowQueryString()}`, init);
  }

  getFallbackRate(range, init = {}) {
    return reportingApiFetch(`${reportingPath("/fallback_rate")}${this._window(range).toWindowQueryString()}`, init);
  }

  getEscalationRate(range, init = {}) {
    return reportingApiFetch(
      `${reportingPath("/escalation_rate")}${this._window(range).toWindowQueryString()}`,
      init,
    );
  }

  getCsatScore(range, init = {}) {
    return reportingApiFetch(`${reportingPath("/csat_score")}${this._window(range).toWindowQueryString()}`, init);
  }

  getThumbsFeedbackStats(range, init = {}) {
    return reportingApiFetch(
      `${reportingPath("/thumbs_feedback_stats")}${this._window(range).toWindowQueryString()}`,
      init,
    );
  }

  getRatingDistribution(range, init = {}) {
    return reportingApiFetch(
      `${reportingPath("/rating_distribution")}${this._window(range).toWindowQueryString()}`,
      init,
    );
  }

  getSentimentDashboard(range, init = {}) {
    return reportingApiFetch(
      `${reportingPath("/sentiment/dashboard")}${this._window(range).toWindowQueryString()}`,
      init,
    );
  }

  /**
   * @param {{ startDate: string, endDate: string }} range
   * @param {{ limit?: number }} [opts]
   * @param {RequestInit} [init]
   */
  getTrendingTopics(range, opts = {}, init = {}) {
    const w = this._window(range);
    const sp = new URLSearchParams();
    sp.set("start_date", w.startDate);
    sp.set("end_date", w.endDate);
    const lim = opts.limit != null ? Math.min(200, Math.max(1, Number(opts.limit))) : 15;
    sp.set("limit", String(lim));
    return reportingApiFetch(`${reportingPath("/trending-topics")}?${sp.toString()}`, init);
  }

  getNegativeComments(range, opts = {}, init = {}) {
    const lim = opts.limit != null ? Math.min(200, Math.max(1, Number(opts.limit))) : 50;
    return reportingApiFetch(
      `${reportingPath("/negative_comments")}${this._window(range).toDaysQueryString({ limit: lim })}`,
      init,
    );
  }

  getUserWordFrequencyTrends(range, opts = {}, init = {}) {
    const lim = opts.limit != null ? Math.min(50, Math.max(1, Number(opts.limit))) : 25;
    return reportingApiFetch(
      `${reportingPath("/user-word-frequency-trends")}${this._window(range).toDaysQueryString({ limit: lim })}`,
      init,
    );
  }
}

export const botReportingApiClient = new BotReportingApiClient();
